// Bolo3K mobile HUD (Wave 2b-2) — thin overlay over the full-viewport
// canvas, mounted on #hud-root (z10, pointer-events:none; interactive
// children opt back in). Landscape-first phone layout:
//
//   top-left      stat strip   (armour/shells/mines/trees bars, K/D, range)
//   top-center    toasts       (connection + wire events via showToast)
//   top-right     minimap chip (tap toggles 112px ↔ 208px)
//   bottom-center LGM chip + build action bar (Farm Road Wall Pill Mine Boat)
//
// The bottom-left quadrant (virtual stick) and bottom-right corner
// (fire/mine buttons) belong to the touch controls (Wave 2b-1) and stay
// clear.
//
// Update model — belt and suspenders: the integrator should call
// MobileHudApi.update() once per rAF frame from main.ts's loop, but the HUD
// also runs its own requestAnimationFrame fallback that self-updates
// whenever no external update() has arrived recently, so the HUD is live
// even before the integration call lands.

import './mobileHud.css';
import type { GameContext } from '../context.js';
import { createBuildBar } from './buildBar.js';
import { createMinimapChip } from './minimapChip.js';
import { createStatStrip } from './statStrip.js';
import { createToasts } from './toasts.js';

/** If update() was called externally within this window, the internal rAF
 *  loop stays passive (no double repaint). */
const EXTERNAL_FRESH_MS = 250;

export interface MobileHudApi {
  /** Repaint the HUD from the current GameContext state. Call once per rAF
   *  frame from the main loop; safe to skip — the HUD self-drives if so. */
  update(): void;
  /** Push a transient top-center toast (connection status, wire events —
   *  the mobile replacement for the scaffold's #status-line). */
  showToast(text: string, ms?: number): void;
  /** Remove all HUD DOM, stop the internal loop, cancel toast timers. */
  dispose(): void;
}

export function initMobileHud(ctx: GameContext, root: HTMLElement): MobileHudApi {
  const statStrip = createStatStrip();
  const minimap = createMinimapChip();
  const buildBar = createBuildBar(ctx);
  const toasts = createToasts();

  root.append(statStrip.el, minimap.el, buildBar.el, toasts.el);

  function doUpdate(): void {
    const state = ctx.state();
    const me = ctx.myTank();
    minimap.update(state);
    statStrip.update(me, state?.version ?? -1);
    buildBar.update(ctx.armedBuildAction(), me?.lgmStatus ?? null);
  }

  // Belt-and-suspenders loop: cheap timestamp check per frame; does real
  // work only while nobody calls api.update().
  let lastExternal = Number.NEGATIVE_INFINITY;
  let disposed = false;
  function loop(now: number): void {
    if (disposed) return;
    if (now - lastExternal > EXTERNAL_FRESH_MS) doUpdate();
    requestAnimationFrame(loop);
  }
  requestAnimationFrame(loop);

  return {
    update() {
      lastExternal = performance.now();
      doUpdate();
    },
    showToast(text, ms) {
      toasts.show(text, ms);
    },
    dispose() {
      disposed = true;
      toasts.dispose();
      statStrip.el.remove();
      minimap.el.remove();
      buildBar.el.remove();
      toasts.el.remove();
    },
  };
}
